import { useEffect, useState } from "react";
import AdminLayout from "layouts/AdminLayout";
import AdminCard from "components/admin/AdminCard";
import StatusBadge from "components/admin/StatusBadge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  AdminTeamUser,
  listAdminTeam,
  createAdminTeamUser,
  updateAdminTeamUser,
  deactivateAdminTeamUser,
} from "services/admin/adminEquipe.service";
import { AdminRole } from "services/admin/adminAuth.types";

const ROLE_LABEL: Record<AdminRole, string> = {
  admin: "Administrador",
  developer: "Desenvolvedor",
  support: "Suporte",
  operations: "Operação",
};

const emptyForm = { name: "", email: "", senha: "", role: "operations" as AdminRole };

const AdminEquipe = () => {
  const [users, setUsers] = useState<AdminTeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    listAdminTeam()
      .then(setUsers)
      .catch((err) => console.error("[AdminEquipe] erro:", err))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.senha.trim()) return;
    setSaving(true);
    try {
      await createAdminTeamUser(form);
      setForm(emptyForm);
      load();
    } catch (err) {
      console.error("[AdminEquipe] erro ao criar usuário:", err);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (user: AdminTeamUser) => {
    if (user.active) {
      await deactivateAdminTeamUser(user.id);
    } else {
      await updateAdminTeamUser(user.id, { active: true });
    }
    load();
  };

  return (
    <AdminLayout title="Equipe" subtitle="Usuários internos da Intrex">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 flex flex-col gap-4">
          <h3 className="text-[13px] font-semibold text-slate-700">Novo usuário</h3>
          <div className="flex flex-col gap-1.5">
            <Label>Nome</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>E-mail</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Senha temporária</Label>
            <Input type="password" value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} required />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Perfil</Label>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as AdminRole })}
            >
              {Object.entries(ROLE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <Button type="submit" disabled={saving} className="bg-brand-sidebar hover:bg-brand-sidebar/90">
            {saving ? "Criando..." : "Criar usuário"}
          </Button>
        </form>

        <div className="xl:col-span-2 flex flex-col gap-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : (
            users.map((user) => (
              <AdminCard key={user.id} className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm text-slate-800">{user.name}</p>
                  <p className="text-sm text-slate-500">{user.email}</p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge label={ROLE_LABEL[user.role]} tone="blue" />
                  <StatusBadge label={user.active ? "Ativo" : "Inativo"} tone={user.active ? "green" : "slate"} />
                  <Button variant="outline" onClick={() => toggleActive(user)}>
                    {user.active ? "Desativar" : "Ativar"}
                  </Button>
                </div>
              </AdminCard>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminEquipe;
